import { useCallback, useEffect, useState } from 'react'
import { sdk } from '@/lib/sdk'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import type { CalculatePriceResponse, PaymentMethod, PublicProduct } from '@/types/checkout'

/**
 * FIN-7: why the server refused a coupon. `message` is the API's own text,
 * already translated; a 429 (too many wrong codes) carries no useful text, so
 * the caller shows its own.
 */
export interface CouponRejection {
  code: string
  status: number | null
  message: string | null
}

interface UsePriceQuoteReturn {
  quote: CalculatePriceResponse | null
  loading: boolean
  /** `calculate-price` failed; the caller decides whether the list price can stand in. */
  failed: boolean
  retry: () => void
  /** The coupon sent with the last request was refused; the quote is then null and `failed` stays false. */
  couponRejection: CouponRejection | null
  /**
   * The API refused to sell this product at all (a 4xx on `productId` or on no field, e.g.
   * the community has no active payout recipient yet, F-12). The text is the
   * API's, already translated; `failed` stays false, since retrying will not help.
   */
  refusal: string | null
}

/**
 * What the buyer will be charged, from `POST …/checkout/calculate-price`.
 *
 * The server owns the math: it knows the fee rules and who pays them. The
 * client only shows the answer. The answer carries the platform fee only when
 * the buyer pays it, and never a provider cost.
 *
 * Requests that arrive out of order are dropped — switching from PIX to card
 * quickly must not leave the PIX quote on screen.
 */
export function usePriceQuote(
  communityId: string | undefined,
  product: PublicProduct | null,
  method: PaymentMethod | null,
  installments: number,
  /** Add-ons bought with a plan (F-37); the quote then itemizes and sums them. */
  addOnProductIds: string[] = [],
  /** FIN-7: a coupon to apply; a refusal comes back as `couponRejection`, not as `failed`. */
  couponCode: string | null = null,
  /** F-93 1B: priced for this company (no trial, its offers only). */
  companyId: string | null = null,
  /** F-116: tickets of one ticket type, priced at the current batch. */
  quantity = 1
): UsePriceQuoteReturn {
  const [quote, setQuote] = useState<CalculatePriceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [couponRejection, setCouponRejection] = useState<CouponRejection | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)

  const productId = product?.id
  // By content: a new array with the same ids must not refetch.
  const addOnKey = addOnProductIds.join(',')

  useEffect(() => {
    if (!communityId || !productId || !method) {
      setQuote(null)
      setFailed(false)
      setRefusal(null)
      return
    }

    let current = true
    setLoading(true)
    setFailed(false)
    setCouponRejection(null)
    setRefusal(null)

    sdk.checkout
      .calculatePrice({
        productId,
        paymentMethod: method,
        installments,
        ...(addOnKey ? { addOnProductIds: addOnKey.split(',') } : {}),
        ...(couponCode ? { couponCode } : {}),
        ...(companyId ? { companyId } : {}),
        ...(quantity > 1 ? { quantity } : {})
      })
      .then((response) => {
        if (!current) return
        setQuote(response && response.totalAmount ? response : null)
        setFailed(!response || !response.totalAmount)
      })
      .catch((error) => {
        if (!current) return
        setQuote(null)
        /* A refused coupon is not a failed quote: the price without it is
           still knowable, and the caller drops the coupon to get it. 429 is
           the API throttling wrong codes, and only coupons trip it. */
        const { message, param, status } = apiErrorMessage(error)
        if (couponCode && (param === 'couponCode' || status === 429)) {
          setCouponRejection({ code: couponCode, status, message })
          return
        }
        /* The product cannot be sold here (F-12: no active recipient; also a
           product no longer active, or payouts not set up, which carry no
           param). Not a failed quote: show the API's reason, not a retry. */
        const aboutProduct = param === 'productId' || param === null || param === 'companyId'
        if (aboutProduct && message && status !== null && status >= 400 && status < 500 && status !== 429) {
          setRefusal(message)
          return
        }
        setFailed(true)
      })
      .finally(() => {
        if (current) setLoading(false)
      })

    return () => {
      current = false
    }
  }, [communityId, productId, method, installments, addOnKey, couponCode, companyId, quantity, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { quote, loading, failed, retry, couponRejection, refusal }
}
